import React, { useState, useEffect } from 'react';
import { getDatabase, ref, push, set, get, onValue } from 'firebase/database';
import { auth } from '../firebase';

const SalesReturnForm = ({ userEmail, onClose, initialData }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState(
    initialData || {
      type: 'Sales Return',
      srnNo: '',
      srnDate: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      billNo: '',
      billDate: '',
      customer: '',
      phone: '',
      items: [], // Initialize with empty array for dynamic rows
      totals: {
        grossAmount: 0,
        discount: 0,
        cgstTax: 0,
        sgstTax: 0,
        igstTax: 0,
        cessTax: 0,
        add: 0,
        less: 0,
        billDiscount: 0,
        roundedOff: 0,
        netAmount: 0,
      },
      createdBy: userEmail || '',
    }
  );
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [products, setProducts] = useState([]);
  const [existingReturns, setExistingReturns] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const salesReturnsRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns`);
    const unsubscribe = onValue(
      salesReturnsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const returnsList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setExistingReturns(returnsList);
        } else {
          setExistingReturns([]);
        }
      },
      (error) => {
        console.error('Error fetching existing returns:', error);
      }
    );
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || initialData) return;
    
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const productsRef = ref(getDatabase(), `users/${encodedEmail}/products`);
    const unsubscribe = onValue(
      productsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const productList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setProducts(productList);
        } else {
          setProducts([]);
        }
      },
      (error) => {
        console.error('Error fetching products:', error);
      }
    );
    return () => unsubscribe();
  }, [currentUser, initialData]);

  useEffect(() => {
    if (!currentUser || initialData) return;
    const generateSRNNumber = async () => {
      try {
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const salesReturnsRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns`);
        const snapshot = await get(salesReturnsRef);
        let maxSRN = 0;
        if (snapshot.exists()) {
          const returns = snapshot.val();
          const srnNumbers = Object.values(returns)
            .map((ret) => parseInt(ret.srnNo, 10))
            .filter((num) => !isNaN(num));
          maxSRN = srnNumbers.length > 0 ? Math.max(...srnNumbers) : 0;
        }
        setFormData((prev) => ({ ...prev, srnNo: (maxSRN + 1).toString() }));
      } catch (error) {
        console.error('Error generating SRN number:', error);
        alert('Error generating SRN number.');
      }
    };
    generateSRNNumber();
  }, [currentUser, initialData]);

  const fetchSalesDataByBillNo = async (billNo) => {
    if (!billNo || !currentUser) return;
    
    const existingReturn = existingReturns.find(ret => 
      ret.billNo === billNo.toString() && 
      (!initialData || ret.id !== initialData.id)
    );
    
    if (existingReturn) {
      setSalesData(null);
      alert(`Sales return already exists for Bill No: ${billNo} (SRN: ${existingReturn.srnNo})`);
      clearItemsData();
      return;
    }
    
    setLoading(true);
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const salesRef = ref(getDatabase(), `users/${encodedEmail}/sales`);
      const snapshot = await get(salesRef);
      if (snapshot.exists()) {
        const salesData = snapshot.val();
        const foundSale = Object.values(salesData).find(
          (sale) => sale.billNo.toString() === billNo.toString()
        );
        if (foundSale) {
          setSalesData(foundSale);
          populateFormWithSalesData(foundSale);
          alert(`Sales data loaded for Bill No: ${billNo}`);
        } else {
          setSalesData(null);
          alert(`No sales data found for Bill No: ${billNo}`);
          clearItemsData();
        }
      } else {
        setSalesData(null);
        alert(`No sales data found for Bill No: ${billNo}`);
        clearItemsData();
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
      alert('Error fetching sales data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const populateFormWithSalesData = (saleData) => {
    const populatedItems = (saleData.items || []).map((item, index) => {
      const product = products.find(p => 
        p.productName === item.itemDescription || 
        p.productName === item.itemName ||
        p.id === item.productId
      );
      
      const totalTaxRate = parseFloat(item.tax) || 0;
      const cgstRate = totalTaxRate / 2;
      const sgstRate = totalTaxRate / 2;
      
      return {
        sNo: index + 1,
        barcode: product?.barcode || item.barcode || item.itemCode || item.productCode || '',
        itemDescription: item.itemDescription || '',
        uom: item.unit || '',
        qty: item.qty || '',
        avgAvailableQty: item.qty || '',
        returnQty: '',
        disc: item.discPercent || '',
        cgst: cgstRate.toString(),
        sgst: sgstRate.toString(),
        cess: item.cess || '0',
        mrp: item.unitRate || '',
        grossAmount: '',
        remarks: item.remarks || '',
        value: '',
      };
    });

    const originalTotals = saleData.totals || {};
    const cgstTax = originalTotals.totalTax ? originalTotals.totalTax / 2 : 0;
    const sgstTax = originalTotals.totalTax ? originalTotals.totalTax / 2 : 0;

    const { items, totals } = calculateTotals(populatedItems);

    setFormData((prev) => ({
      ...prev,
      billDate: saleData.date || '',
      customer: saleData.customer || '',
      phone: saleData.phone || saleData.phoneNumber || saleData.customerPhone || saleData.mobile || saleData.mobileNo || '',
      items: items, // Set items dynamically based on sales data
      totals: {
        grossAmount: parseFloat(totals.grossAmount) || parseFloat(originalTotals.grossAmt) || 0,
        discount: parseFloat(totals.discount) || parseFloat(originalTotals.discount) || 0,
        cgstTax: parseFloat(totals.cgstTax) || parseFloat(cgstTax) || 0,
        sgstTax: parseFloat(totals.sgstTax) || parseFloat(sgstTax) || 0,
        igstTax: parseFloat(totals.igstTax) || 0,
        cessTax: parseFloat(totals.cessTax) || 0,
        add: 0,
        less: 0,
        billDiscount: parseFloat(totals.billDiscount) || parseFloat(originalTotals.billDiscount) || 0,
        roundedOff: parseFloat(totals.roundedOff) || 0,
        netAmount: parseFloat(totals.netAmount) || parseFloat(originalTotals.netAmount) || 0,
      },
    }));
  };

  const clearItemsData = () => {
    setFormData((prev) => ({
      ...prev,
      billDate: '',
      customer: '',
      phone: '',
      items: [], // Clear items to empty array
      totals: {
        grossAmount: 0,
        discount: 0,
        cgstTax: 0,
        sgstTax: 0,
        igstTax: 0,
        cessTax: 0,
        add: 0,
        less: 0,
        billDiscount: 0,
        roundedOff: 0,
        netAmount: 0,
      },
    }));
  };

  const handleBillNoChange = (e) => {
    const billNo = e.target.value;
    setFormData((prev) => ({ ...prev, billNo }));
    
    if (billNo.trim()) {
      const timeoutId = setTimeout(() => {
        fetchSalesDataByBillNo(billNo.trim());
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setSalesData(null);
      clearItemsData();
    }
  };

  const calculateTotals = (items) => {
    let grossAmount = 0;
    let cgstTax = 0;
    let sgstTax = 0;
    let igstTax = 0;
    let cessTax = 0;
    let discount = 0;

    const updatedItems = items.map((item) => {
      const returnQty = parseFloat(item.returnQty) || 0;
      const mrp = parseFloat(item.mrp) || 0;
      const discPercent = parseFloat(item.disc) || 0;
      
      // Calculate gross amount (Return Qty × MRP)
      const itemGrossAmount = returnQty * mrp;
      const itemDiscount = (itemGrossAmount * discPercent) / 100;
      const value = itemGrossAmount - itemDiscount;
      
      grossAmount += itemGrossAmount; // Add to total gross amount
      discount += itemDiscount;

      const cgstRate = parseFloat(item.cgst) || 0;
      const sgstRate = parseFloat(item.sgst) || 0;
      const cessRate = parseFloat(item.cess) || 0;

      const cgstAmount = (value * cgstRate) / 100;
      const sgstAmount = (value * sgstRate) / 100;
      const cessAmount = (value * cessRate) / 100;

      cgstTax += cgstAmount;
      sgstTax += sgstAmount;
      cessTax += cessAmount;

      return { 
        ...item, 
        value: value.toFixed(2), 
        grossAmount: itemGrossAmount.toFixed(2) 
      };
    });

    const totalTaxAmount = cgstTax + sgstTax + igstTax + cessTax;
    const netAmount = grossAmount - discount + totalTaxAmount; // Use grossAmount in calculation
    const roundedOff = 0; // Remove rounding to fix calculation
    const finalNetAmount = parseFloat(netAmount.toFixed(2)); // Keep precise calculation

    return {
      items: updatedItems,
      totals: {
        grossAmount: parseFloat(grossAmount.toFixed(2)), // This will now show the sum of all gross amounts
        discount: parseFloat(discount.toFixed(2)),
        cgstTax: parseFloat(cgstTax.toFixed(2)),
        sgstTax: parseFloat(sgstTax.toFixed(2)),
        igstTax: parseFloat(igstTax.toFixed(2)),
        cessTax: parseFloat(cessTax.toFixed(2)),
        add: 0,
        less: 0,
        billDiscount: 0,
        roundedOff: parseFloat(roundedOff.toFixed(2)),
        netAmount: parseFloat(finalNetAmount.toFixed(2)),
      },
    };
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    
    if (name === 'billNo') {
      handleBillNoChange(e);
      return;
    }

    if (index !== undefined) {
      const updatedItems = [...formData.items];
      updatedItems[index][name] = value;
      
      if (name === "returnQty") {
        const returnQty = parseFloat(value) || 0;
        const availableQty = parseFloat(updatedItems[index].avgAvailableQty) || 0;
        if (returnQty > availableQty) {
          updatedItems[index].returnQty = availableQty.toString();
          alert(`Return quantity cannot exceed available quantity (${availableQty}).`);
        }
      }

      const { items, totals } = calculateTotals(updatedItems);
      setFormData((prev) => ({ ...prev, items, totals }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert('Please log in to save sales return data.');
      return;
    }

    try {
      setLoading(true);
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const salesReturnsRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns`);
      
      const returnData = {
        ...formData,
        createdBy: currentUser.email,
        createdAt: new Date().toISOString(),
        originalSalesData: salesData
          ? {
              billNo: salesData.billNo,
              date: salesData.date,
              customer: salesData.customer,
              netAmount: salesData.totals?.netAmount,
            }
          : null,
      };

      if (initialData && initialData.id) {
        const returnRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns/${initialData.id}`);
        await set(returnRef, returnData);
        alert('Sales Return updated successfully!');
      } else {
        await push(salesReturnsRef, returnData);
        alert('Sales Return saved successfully!');
      }

      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving sales return:', error);
      alert('Error saving data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentUser || !initialData || !initialData.id) {
      alert('No existing return selected for update.');
      return;
    }

    try {
      setLoading(true);
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const returnRef = ref(getDatabase(), `users/${encodedEmail}/salesReturns/${initialData.id}`);
      
      const returnData = {
        ...formData,
        createdBy: currentUser.email,
        updatedAt: new Date().toISOString(),
        originalSalesData: salesData
          ? {
              billNo: salesData.billNo,
              date: salesData.date,
              customer: salesData.customer,
              netAmount: salesData.totals?.netAmount,
            }
          : null,
      };

      await set(returnRef, returnData);
      alert('Sales Return updated successfully!');
      
      if (onClose) onClose();
    } catch (error) {
      console.error('Error updating sales return:', error);
      alert('Error updating data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    formContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid #e2e8f0',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    closeButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#dc3545',
      color: '#ffffff',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    headerSection: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
    },
    inputGroup: {
      flex: '1',
      minWidth: '150px',
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem',
      display: 'block',
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#374151',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box',
    },
    input1: {
      width: '12%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#374151',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#374151',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      overflow: 'auto',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '2rem',
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
      tableLayout: 'fixed',
      minWidth: '1300px', // Increased for the new column
    },
    th: {
      padding: '1rem 0.75rem',
      textAlign: 'left',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    },
    td: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      overflow: 'hidden',
    },
    tdInput: {
      width: '100%',
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#374151',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box',
      height: '36px',
      lineHeight: '1.5',
    },
    tdInputTextWrap: {
      width: '100%',
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#374151',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box',
      height: '36px',
      lineHeight: '1.5',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },
    totalsContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '2rem',
    },
    totalsBox: {
      width: '200px',
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    },
    totalRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.75rem',
      fontSize: '0.9rem',
      color: '#374151',
    },
    totalLabel: {
      fontWeight: '500',
    },
    totalInput: {
      width: '120px',
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      textAlign: 'right',
      fontSize: '0.9rem',
      backgroundColor: '#f8f9fa',
      boxSizing: 'border-box',
    },
    netAmountRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderRadius: '8px',
      marginTop: '1rem',
      fontWeight: '600',
      fontSize: '1rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end',
    },
    button: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
      transform: 'translateY(0)',
    },
    saveButton: {
      backgroundColor: '#16a34a',
      color: '#ffffff',
    },
    updateButton: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    cancelButton: {
      backgroundColor: '#dc3545',
      color: '#ffffff',
    },
    loadingIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      color: '#718096',
      marginBottom: '1.5rem',
    },
    salesDataInfo: {
      padding: '1rem 1.5rem',
      backgroundColor: '#e7f3fe',
      border: '1px solid #bfdbfe',
      borderRadius: '12px',
      marginBottom: '1.5rem',
    },
    salesDataTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#2563eb',
      marginBottom: '0.5rem',
    },
    salesDataDetails: {
      fontSize: '0.9rem',
      color: '#374151',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to access the Sales Return Form</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Sales Return Form</h1>
        {onClose && (
          <button
            style={styles.closeButton}
            onClick={handleCancel}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
            }}
          >
            Close
          </button>
        )}
      </div>
      <div style={styles.formContainer}>
        {loading && (
          <div style={styles.loadingIndicator}>
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #2563eb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            ></div>
            <span>Loading sales data...</span>
          </div>
        )}

        {salesData && (
          <div style={styles.salesDataInfo}>
            <div style={styles.salesDataTitle}>Original Sales Data Found</div>
            <div style={styles.salesDataDetails}>
              <strong>Bill No:</strong> {salesData.billNo} | <strong>Date:</strong> {salesData.date} |{' '}
              <strong>Customer:</strong> {salesData.customer} |{' '}
              <strong>Net Amount:</strong> ₹{salesData.totals?.netAmount?.toFixed(2) || '0.00'}
            </div>
          </div>
        )}

        <form>
          <div style={styles.headerSection}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Type</label>
              <select name="type" value={formData.type} onChange={handleChange} style={styles.select}>
                <option value="Sales Return">Sales Return</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Bill No *</label>
              <input
                type="text"
                name="billNo"
                value={formData.billNo}
                onChange={handleBillNoChange}
                style={styles.input}
                placeholder="Enter bill number"
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Bill Date</label>
              <input
                type="text"
                name="billDate"
                value={formData.billDate}
                onChange={handleChange}
                style={styles.input}
                readOnly
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Customer</label>
              <input
                type="text"
                name="customer"
                value={formData.customer}
                onChange={handleChange}
                style={styles.input}
                readOnly
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>SRN No</label>
              <input
                type="text"
                name="srnNo"
                value={formData.srnNo}
                onChange={handleChange}
                style={styles.input}
                readOnly
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>SRN Date</label>
              <input
                type="text"
                name="srnDate"
                value={formData.srnDate}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                style={styles.input1}
                readOnly
              />
            </div>
          </div>

          {formData.items.length > 0 ? (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: '5%' }}>S.No</th>
                    <th style={{ ...styles.th, width: '18%' }}>Barcode</th>
                    <th style={{ ...styles.th, width: '25%' }}>Item Description</th>
                    <th style={{ ...styles.th, width: '8%' }}>UOM</th>
                    <th style={{ ...styles.th, width: '7%' }}>Qty</th>
                    <th style={{ ...styles.th, width: '16%' }}>Avail Qty</th>
                    <th style={{ ...styles.th, width: '20%' }}>Return Qty</th>
                    <th style={{ ...styles.th, width: '12%' }}>Disc%</th>
                    <th style={{ ...styles.th, width: '12%' }}>CGST%</th>
                    <th style={{ ...styles.th, width: '12%' }}>SGST%</th>
                    <th style={{ ...styles.th, width: '12%' }}>CESS%</th>
                    <th style={{ ...styles.th, width: '14%' }}>MRP</th>
                    <th style={{ ...styles.th, width: '22%' }}>Gross Amount</th>
                    <th style={{ ...styles.th, width: '14%' }}>Remarks</th>
                    <th style={{ ...styles.th, width: '12%' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f7fafc' : '#ffffff',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <td style={styles.td}>{item.sNo}</td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          name="barcode"
                          value={item.barcode}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          name="itemDescription"
                          value={item.itemDescription}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInputTextWrap}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          name="uom"
                          value={item.uom}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          name="qty"
                          value={item.qty}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          readOnly
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          name="avgAvailableQty"
                          value={item.avgAvailableQty}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          readOnly
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          name="returnQty"
                          value={item.returnQty}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          name="disc"
                          value={item.disc}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          name="cgst"
                          value={item.cgst}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          name="sgst"
                          value={item.sgst}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          name="cess"
                          value={item.cess}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          name="mrp"
                          value={item.mrp}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          value={((parseFloat(item.returnQty) || 0) * (parseFloat(item.mrp) || 0)).toFixed(2)}
                          style={{ ...styles.tdInputTextWrap, backgroundColor: '#f8f9fa' }}
                          readOnly
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          name="remarks"
                          value={item.remarks}
                          onChange={(e) => handleChange(e, index)}
                          style={styles.tdInput}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          value={item.value}
                          style={{ ...styles.tdInputTextWrap, backgroundColor: '#f8f9fa' }}
                          readOnly
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '1rem', textAlign: 'center', backgroundColor: '#e7f3fe', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '12px', marginBottom: '1.5rem' }}>
              No items to display. Enter a valid Bill No to load items.
            </div>
          )}

          <div style={styles.totalsContainer}>
            <div style={styles.totalsBox}>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Gross Amount</span>
                <input
                  type="text"
                  value={formData.totals.grossAmount.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Discount</span>
                <input
                  type="text"
                  value={formData.totals.discount.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>CGST Tax</span>
                <input
                  type="text"
                  value={formData.totals.cgstTax.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>SGST Tax</span>
                <input
                  type="text"
                  value={formData.totals.sgstTax.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>IGST Tax</span>
                <input
                  type="text"
                  value={formData.totals.igstTax.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>CESS Tax</span>
                <input
                  type="text"
                  value={formData.totals.cessTax.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Bill Discount</span>
                <input
                  type="text"
                  value={formData.totals.billDiscount.toFixed(2)}
                  style={styles.totalInput}
                  readOnly
                />
              </div>
             
              <div style={styles.netAmountRow}>
                <span>Net Amount</span>
                <span>₹{formData.totals.netAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div style={styles.buttonContainer}>
            <button
              type="submit"
              style={{ ...styles.button, ...styles.saveButton }}
              disabled={loading}
              onClick={handleSubmit}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(22, 163, 74, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(22, 163, 74, 0.3)';
                }
              }}
            >
              {loading ? 'Saving...' : 'Save Return'}
            </button>
            {initialData && initialData.id && (
              <button
                type="button"
                style={{ ...styles.button, ...styles.updateButton }}
                disabled={loading}
                onClick={handleUpdate}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                  }
                }}
              >
                {loading ? 'Updating...' : 'Update Return'}
              </button>
            )}
            <button
              type="button"
              onClick={handleCancel}
              style={{ ...styles.button, ...styles.cancelButton }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        input:focus,
        select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        button:hover:not(:disabled) {
          filter: brightness(90%);
        }
        table {
          min-width: 1300px;
        }
        td {
          position: relative;
        }
        input[type="text"],
        input[type="number"],
        input[type="date"] {
          width: 100%;
          box-sizing: border-box;
        }
        input[type="date"]::-webkit-datetime-edit-text,
        input[type="date"]::-webkit-datetime-edit-month-field,
        input[type="date"]::-webkit-datetime-edit-day-field,
        input[type="date"]::-webkit-datetime-edit-year-field {
          color: #374151;
        }
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(50%) sepia(50%) saturate(200%) hue-rotate(200deg);
        }
        @media (max-width: 768px) {
          .headerSection {
            flex-direction: column;
          }
          .inputGroup {
            width: 100%;
          }
          .buttonContainer {
            flex-direction: column;
            align-items: stretch;
          }
          .button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesReturnForm;